import { createClient } from '@supabase/supabase-js'

// Supabase configuration
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
  }
})

// Helper function to get current authenticated user
export const getCurrentUser = async () => {
  try {
    const { data: { user }, error } = await supabase.auth.getUser()
    if (error) throw error
    return { user, error: null }
  } catch (error) {
    console.error('Error getting current user:', error.message)
    return { user: null, error }
  }
}

// Helper function to get user's accessible farms
export const getUserFarms = async (authUserId) => {
  try {
    console.log('📊 Fetching farms for auth user:', authUserId);
    
    // CRITICAL: The database uses integer user_id, not Supabase Auth UUID
    // We need to map Auth UUID → integer user_id via email
    
    // Step 1: Get user email from Supabase Auth
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError || !user) {
      console.error('❌ Cannot get authenticated user');
      return { data: [], error: userError };
    }
    
    const email = user.email;
    console.log('📧 User email:', email);
    
    // Step 2: Look up integer user_id from user table by email
    const { data: userData, error: userLookupError } = await supabase
      .from('user')
      .select('id')
      .eq('email', email)
      .maybeSingle();
    
    if (userLookupError) {
      console.error('❌ Error looking up user by email:', userLookupError);
      return { data: [], error: userLookupError };
    }
    
    if (!userData) {
      console.warn('⚠️ No user found with email:', email);
      return { data: [], error: null };
    }
    
    const integerUserId = userData.id;
    console.log('✅ Mapped to integer user_id:', integerUserId);
    
    // Step 3: Query farm_user_role_view with integer user_id
    const { data, error } = await supabase
      .from('farm_user_role_view')
      .select('*')
      .eq('user_id', integerUserId);
    
    if (error) {
      console.error('❌ Error fetching farms:', error);
      throw error;
    }
    
    console.log('📊 Raw query result:', data?.length, 'records');
    
    // Transform data to expected format
    const transformedFarms = data?.map(item => ({
      farm_id: item.farm_id,
      farm_name: item.farm_name || 'Unnamed Farm',
      farm_size: 0, // Add if available in view
      role: item.role_name || 'member',
      id: item.farm_id
    })) || [];
    
    console.log('📊 Transformed farms:', transformedFarms);
    console.log('📊 Total farms found:', transformedFarms.length);
    
    return { data: transformedFarms, error: null };
  } catch (error) {
    console.error('❌ Error getting user farms:', error.message);
    return { data: [], error };
  }
}

// Helper function to sign in with email and password
export const signIn = async (email, password) => {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    })
    
    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error signing in:', error.message)
    return { data: null, error }
  }
}

// Helper function to sign out
export const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
    return { error: null }
  } catch (error) {
    console.error('Error signing out:', error.message)
    return { error }
  }
}

// Helper function to get ALL farms (for admin users)
export const getAllFarms = async () => {
  try {
    console.log('👑 Admin: Fetching ALL farms...');
    
    const { data, error } = await supabase
      .from('farm')
      .select(`
        id,
        farmName,
        farmsize,
        created_at,
        address,
        organisation (
          id,
          name_org
        )
      `)
      .order('farmName', { ascending: true });
    
    if (error) {
      console.error('❌ Error fetching all farms:', error);
      throw error;
    }
    
    console.log('📊 Total farms found:', data?.length || 0);
    
    // Transform data to expected format
    const transformedFarms = data?.map(item => ({
      farm_id: item.id,
      farm_name: item.farmName || 'Unnamed Farm',
      farm_size: item.farmsize || 0,
      farmsize: item.farmsize || 0,
      role: 'admin',
      id: item.id,
      address: item.address || '',
      organisation: item.organisation?.id || null,
      organisation_name: item.organisation?.name_org || 'N/A'
    })) || [];
    
    return { data: transformedFarms, error: null };
  } catch (error) {
    console.error('❌ Error getting all farms:', error.message);
    return { data: [], error };
  }
}

// Get farms for a specific organization via user_org_farm table
// Uses RPC function with SECURITY DEFINER to bypass RLS
export const getFarmsByOrgViaUserOrgFarm = async (orgId) => {
  try {
    const { data, error } = await supabase.rpc('get_farms_by_org_via_user_org_farm', {
      org_id: orgId
    });
    
    if (error) {
      console.error('❌ Error fetching org farms via RPC:', error.message);
      return { data: [], error };
    }
    
    // Transform to match farm format
    const farms = data?.map(row => ({
      farm_id: row.farm_id,
      farm_name: row.farm_name,
      farmsize: row.farmsize,
      organisation: row.organisation
    })) || [];
    
    console.log(`📊 Found ${farms.length} farms for org ${orgId} via RPC`);
    return { data: farms, error: null };
  } catch (error) {
    console.error('❌ Error getting org farms:', error.message);
    return { data: [], error };
  }
}

// Get farm count for each organization (combining both sources)
// Uses RPC function with SECURITY DEFINER to bypass RLS
export const getOrgFarmCounts = async () => {
  try {
    const { data, error } = await supabase.rpc('get_org_farm_counts');
    
    if (error) {
      console.error('Error fetching org farm counts:', error);
      return {};
    }
    
    // Convert array to object keyed by organisation_id
    const result = {};
    data?.forEach(row => {
      result[String(row.organisation_id)] = row.farm_count;
    });
    
    console.log('📊 Org farm counts loaded via RPC:', Object.keys(result).length, 'orgs with farms');
    return result;
  } catch (error) {
    console.error('Error getting org farm counts:', error);
    return {};
  }
}

// Test connection to Supabase
export const testConnection = async () => {
  try {
    // Try to query a simple table to test connection
    const { data, error } = await supabase
      .from('country')
      .select('count')
      .limit(1)
    
    if (error) throw error
    
    console.log('✅ Supabase connection successful!')
    return { success: true, error: null }
  } catch (error) {
    console.error('❌ Supabase connection failed:', error.message)
    return { success: false, error }
  }
}
